import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import { cdnDomain } from "@/constants/cdnUrls";
import { dateFormats } from "@/utils/dateFormatter";

export default function PopularRentalCarCard({
  car,
  handleClick,
  horizontal,
  index,
  useRawSpecLabels = false,
}) {
  const t = useTranslationDetector();
  const { formatPrice } = usePriceFormatter();

  const companyLogoUrl =
    car?.CompanyImageUrl ||
    `${cdnDomain}/Images/Car/CarCompanies/${car?.Company?.LogoSlug}.png`;

  const vehicleImageUrl =
    car?.VehicleImages?.[0]?.ImageUrl || car?.ImageUrl || "";

  const transmissionLabel = useRawSpecLabels
    ? car?.Transmission || ""
    : t(`Car.${car.Transmission}`);

  const fuelTypeLabel = useRawSpecLabels
    ? car?.FuelType || ""
    : t(`Car.${car.FuelType}`);
  const pickUpLocationNameRaw =
    car?.PickUpLocationName ||
    car?.PickupLocationName ||
    car?.pickUpLocationName ||
    car?.PickUpLocation?.Name ||
    car?.pickupLocation?.name ||
    "";
  const pickUpLocationSlug =
    car?.PickUpLocationSlug || car?.pickupLocationSlug || "";
  const pickUpLocationName =
    (typeof pickUpLocationNameRaw === "string"
      ? pickUpLocationNameRaw.trim()
      : "") ||
    (typeof pickUpLocationSlug === "string"
      ? pickUpLocationSlug.replace(/-/g, " ").trim()
      : "");
  const pickUpDate =
    car?.pickupdate ||
    car?.PickUpDate ||
    car?.pickupDate ||
    car?.PickUpDateTime;
  const displayPickUpDate = pickUpDate
    ? dateFormats.dayShortMonthShortDay(pickUpDate, "tr")
    : "";
  const hasPickupMeta = Boolean(pickUpLocationName);

  const properties = (
    <ul
      className={
        horizontal
          ? "flex flex-col items-start justify-center gap-3"
          : "flex items-center justify-between"
      }
    >
      <li className="inline-flex items-center gap-2">
        <FontAwesomeIcon
          className="h-5 w-5"
          icon={byPrefixAndName.fak["custom-car-gear"]}
        />
        <span className="text-sm">{transmissionLabel}</span>
      </li>
      {!horizontal && (
        <li className="h-3/5 w-[1px] border-r border-solid border-[var(--color-primary-weak)]" />
      )}
      <li className="inline-flex items-center gap-2">
        <FontAwesomeIcon
          className="h-5 w-5"
          icon={byPrefixAndName.fak["gas-pump"]}
        />
        <span className="text-sm">{fuelTypeLabel}</span>
      </li>
      {!horizontal && (
        <li className="h-3/5 w-[1px] border-r border-solid border-[var(--color-primary-weak)]" />
      )}
      <li className="inline-flex items-center gap-2">
        <FontAwesomeIcon
          className="h-5 w-5"
          icon={byPrefixAndName.fak["gauge-max"]}
        />
        <span className="text-sm">
          {Number(car?.MileageLimit || 0) > 0
            ? `${car.MileageLimit} Km`
            : t("Car.limitlessMileage")}
        </span>
      </li>
    </ul>
  );

  return (
    <div
      data-car-item={car?.Reference || index}
      data-car-item-index={index}
      className={`flex w-full cursor-pointer flex-col justify-between rounded-[10px] border border-solid border-[var(--color-primary-weak)] ${
        hasPickupMeta ? "min-h-[343px] px-6 py-4" : "h-[355px] p-6"
      }`}
      onClick={() => handleClick(car)}
    >
      {hasPickupMeta && (
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex min-w-0 items-center gap-1 text-sm leading-[19px] font-medium text-[var(--foreground-default,#1D1E20)]">
            <FontAwesomeIcon
              icon={byPrefixAndName.far["location-dot"]}
              className="h-4 w-4 shrink-0"
            />
            <span className="truncate">{pickUpLocationName}</span>
          </div>
          {displayPickUpDate ? (
            <span className="text-sm leading-[19px] font-normal text-[var(--foreground-default,#1D1E20)]">
              {displayPickUpDate}
            </span>
          ) : null}
        </div>
      )}
      {hasPickupMeta && (
        <div className="my-4 border-t border-solid border-[var(--primary-weaker,#F2F2F9)]" />
      )}
      <div className="flex justify-between gap-3">
        <div>
          <div className="lg font-semibold">{`${car.Brand} ${car.Model}`}</div>
          <span className="text-primary-muted-subtext text-sm">
            {t("Car.OrASimilarVehicle")}
          </span>
        </div>
        <figure>
          <Image
            src={companyLogoUrl}
            width={66}
            height={35}
            alt={`${car.Company.Name}`}
          />
        </figure>
      </div>
      <div
        className={
          horizontal ? "lg:flex lg:flex-row-reverse lg:justify-between" : ""
        }
      >
        <figure
          className={`flex items-center justify-center ${horizontal ? "h-[166px]" : "h-[138px] py-[14px]"}`}
        >
          <Image
            src={vehicleImageUrl}
            width={0}
            height={0}
            sizes="100%"
            alt={`${car.Brand} ${car.Model}`}
            className={`h-full w-full object-contain ${horizontal ? "max-lg:max-w-[230px]" : "h-full"}`}
          />
        </figure>

        {horizontal && properties}
      </div>
      {!horizontal && properties}
      <div
        className={`${hasPickupMeta ? "my-4" : ""} border-t border-solid border-[var(--color-primary-weak)]`}
      />
      <div>
        <div className="text-primary-muted-subtext text-sm">
          {t.rich("Car.popularPriceStartingFrom", {
            0: formatPrice(car.DailyPrice, car.CurrencyCode, 0),
            strong: (children) => (
              <strong className="text-foreground text-xl font-semibold">
                {children}
              </strong>
            ),
          })}
        </div>
      </div>
    </div>
  );
}
